import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { hrApi } from '@/api';
import { EmployeeSelect, HrShiftSelect } from '@/components/selects';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { DatePicker } from '@/components/ui/DatePicker';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { FormGrid } from '@/components/ui/FormGrid';
import { RecordDataTable } from '@/components/ui/RecordDataTable';
import { dataTablePageProps, paginateClientList, usePagination } from '@/hooks/usePagination';
import type { HrShift } from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

function SectionTable({ children }: { children: ReactNode }) {
  return <div className="border-t border-slate-200 pt-4 dark:border-slate-700">{children}</div>;
}

export function HrShiftsPage() {
  const shiftsPagination = usePagination();
  const [shifts, setShifts] = useState<HrShift[]>([]);
  const [overtime, setOvertime] = useState<unknown[]>([]);
  const [roster, setRoster] = useState<unknown[]>([]);

  const [shiftName, setShiftName] = useState('');
  const [shiftStart, setShiftStart] = useState('09:00');
  const [shiftEnd, setShiftEnd] = useState('18:00');

  const [employeeId, setEmployeeId] = useState('');
  const [shiftId, setShiftId] = useState('');
  const [rosterDate, setRosterDate] = useState('');

  const [otEmployeeId, setOtEmployeeId] = useState('');
  const [otDate, setOtDate] = useState('');
  const [otHours, setOtHours] = useState('2');

  const load = useCallback(async () => {
    try {
      const shiftRows = await hrApi.listShifts();
      setShifts(shiftRows);
      shiftsPagination.applyClientCount(shiftRows.length);
      setOvertime(await hrApi.listOvertime());
      setRoster(await hrApi.listRoster());
    } catch (err) {
      showApiError(err, 'Failed to load shift data');
    }
  }, [shiftsPagination]);

  useEffect(() => {
    load();
  }, [load]);

  const createShift = async () => {
    if (!shiftName.trim()) return;
    try {
      await hrApi.createShift({
        name: shiftName.trim(),
        shiftType: 'FIXED',
        startTime: shiftStart,
        endTime: shiftEnd,
      });
      notify.success('Shift created');
      setShiftName('');
      load();
    } catch (err) {
      showApiError(err, 'Create shift failed');
    }
  };

  const assignRoster = async () => {
    if (!employeeId || !shiftId || !rosterDate) return;
    try {
      await hrApi.assignRoster({
        employeeId: Number(employeeId),
        shiftId: Number(shiftId),
        rosterDate,
      });
      notify.success('Roster assigned');
      setEmployeeId('');
      setShiftId('');
      setRosterDate('');
      load();
    } catch (err) {
      showApiError(err, 'Roster assign failed');
    }
  };

  const requestOt = async () => {
    if (!otEmployeeId || !otDate || !otHours) return;
    try {
      await hrApi.requestOvertime({
        employeeId: Number(otEmployeeId),
        overtimeDate: otDate,
        minutes: Number(otHours) * 60,
      });
      notify.success('Overtime requested');
      setOtEmployeeId('');
      setOtDate('');
      setOtHours('2');
      load();
    } catch (err) {
      showApiError(err, 'Overtime request failed');
    }
  };

  const shiftCols: Column<HrShift>[] = [
    { key: 'id', header: '#' },
    { key: 'name', header: 'Name' },
    { key: 'shiftType', header: 'Type' },
    { key: 'startTime', header: 'Start' },
    { key: 'endTime', header: 'End' },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Shifts & roster"
        description="Shift definitions, roster assignments, and overtime"
        breadcrumbs={[{ label: 'HR', to: '/hr' }, { label: 'Shifts' }]}
      />

      <section className="card space-y-4 p-4">
        <div>
          <h3 className="font-semibold text-slate-900 dark:text-slate-100">Shift definitions</h3>
          <p className="mt-1 text-sm text-slate-500">Create fixed shifts, then assign them on the roster below.</p>
        </div>
        <FormGrid columns={3}>
          <Input label="Shift name" value={shiftName} onChange={(e) => setShiftName(e.target.value)} />
          <Input label="Start time" type="time" value={shiftStart} onChange={(e) => setShiftStart(e.target.value)} />
          <Input label="End time" type="time" value={shiftEnd} onChange={(e) => setShiftEnd(e.target.value)} />
          <Button className="self-end" onClick={() => void createShift()}>
            Create shift
          </Button>
        </FormGrid>
        <SectionTable>
          <DataTable
            columns={shiftCols}
            data={paginateClientList(shifts, shiftsPagination.page, shiftsPagination.size)}
            keyField="id"
            emptyTitle="No shifts yet"
            {...dataTablePageProps(shiftsPagination)}
          />
        </SectionTable>
      </section>

      <section className="card space-y-4 p-4">
        <div>
          <h3 className="font-semibold text-slate-900 dark:text-slate-100">Roster assignments</h3>
          <p className="mt-1 text-sm text-slate-500">Assign an employee to a shift for a specific date.</p>
        </div>
        <FormGrid columns={3}>
          <EmployeeSelect value={employeeId} onChange={setEmployeeId} />
          <HrShiftSelect value={shiftId} onChange={setShiftId} />
          <DatePicker label="Date" value={rosterDate} onChange={setRosterDate} />
          <Button className="self-end" onClick={() => void assignRoster()}>
            Assign roster
          </Button>
        </FormGrid>
        <SectionTable>
          <RecordDataTable data={roster} embedded emptyTitle="No roster assignments yet" />
        </SectionTable>
      </section>

      <section className="card space-y-4 p-4">
        <div>
          <h3 className="font-semibold text-slate-900 dark:text-slate-100">Overtime requests</h3>
          <p className="mt-1 text-sm text-slate-500">Submit overtime hours for approval and payroll.</p>
        </div>
        <FormGrid columns={3}>
          <EmployeeSelect value={otEmployeeId} onChange={setOtEmployeeId} />
          <DatePicker label="Date" value={otDate} onChange={setOtDate} />
          <Input label="Hours" type="number" min={0.5} step={0.5} value={otHours} onChange={(e) => setOtHours(e.target.value)} />
          <Button className="self-end" onClick={() => void requestOt()}>
            Request overtime
          </Button>
        </FormGrid>
        <SectionTable>
          <RecordDataTable data={overtime} embedded emptyTitle="No overtime requests yet" />
        </SectionTable>
      </section>
    </div>
  );
}
